import React, { useContext, useMemo } from "react";
import { Internals, TimelineContextValue, useVideoConfig } from "remotion";
import { useTranslation } from "react-i18next";

type PremountProps = {
  for: number;
  children: React.ReactNode;
};

export const Premount: React.FC<PremountProps> = ({ for: premountFor, children }) => {
  const { t } = useTranslation();
  const sequenceContext = useContext(Internals.SequenceContext);
  const { id } = useVideoConfig();
  if (typeof premountFor === "undefined") {
    throw new Error(t("page:error.premountForPropMissing"));
  }

  if (typeof premountFor !== "number") {
    throw new Error(t("page:error.premountForPropType", { type: typeof premountFor }));
  }

  if (Number.isNaN(premountFor)) {
    throw new Error(t("page:error.premountForPropNaN"));
  }

  if (!Number.isFinite(premountFor)) {
    throw new Error(t("page:error.premountForPropNotFinite", { value: premountFor }));
  }
  const context = useContext(Internals.Timeline.TimelineContext);
  const isPremount = useMemo(() => {
    const contextOffset = sequenceContext ? sequenceContext.cumulatedFrom + sequenceContext.relativeFrom : 0;
    const currentFrame = (context.frame[id] ?? 0) - contextOffset;
    return currentFrame < premountFor;
  }, [premountFor, sequenceContext, context, id]);

  const value: TimelineContextValue = useMemo(() => {
    const contextOffset = sequenceContext ? sequenceContext.cumulatedFrom + sequenceContext.relativeFrom : 0;

    const currentFrame = (context.frame[id] ?? 0) - contextOffset;
    return {
      ...context,
      playing: currentFrame < premountFor ? false : context.playing,
      imperativePlaying: {
        current: currentFrame < premountFor ? false : context.imperativePlaying.current,
      },
      frame: { [id]: Math.max(0, currentFrame - premountFor) + contextOffset },
      isPremount: isPremount,
    };
  }, [context, premountFor, sequenceContext, id, isPremount]);

  return (
    <Internals.Timeline.TimelineContext.Provider value={value}>
      <div className="player-wrapper" style={{ opacity: isPremount ? 0 : 1, display: "flex" }}>
        {children}
      </div>
    </Internals.Timeline.TimelineContext.Provider>
  );
};
